import React, { useState, useRef } from 'react';
import TaskRow from './TaskRow';

// Editable Task Row Component (wrapper that includes subtask management)
// This component is shared between SingleProjectView and MyTasksView
const EditableTaskRow = ({ 
    task, 
    project, 
    taskListId, // Optional - for SingleProjectView
    onUpdate, 
    onDelete, 
    addAssigneeToTask, 
    searchUsers, 
    onOpenDetail, 
    onCreateSubtask, 
    onUpdateSubtask, 
    onDeleteSubtask, 
    projectId,
    // Drag and drop props (optional - for SingleProjectView)
    onDragStart,
    onDragOver,
    onDrop,
    onDragEnd,
    isDragging,
    isDragOver,
    DefaultShowSubtasks = false,
}) => {
    const [showSubtasks, setShowSubtasks] = useState(DefaultShowSubtasks);
    const [isAddingSubtask, setIsAddingSubtask] = useState(false);
    const [subtaskTitle, setSubtaskTitle] = useState('');
    const subtaskInputRef = useRef(null);

    // Helper function to map assignee data (for SingleProjectView compatibility)
    const mapAssignee = (assigneeData) => {
        if (!assigneeData) return null;
        return {
            ID: assigneeData.user_id || assigneeData.ID || assigneeData.id,
            id: assigneeData.user_id || assigneeData.ID || assigneeData.id,
            display_name: assigneeData.display_name || assigneeData.name,
            name: assigneeData.display_name || assigneeData.name,
            image: assigneeData.assignee || assigneeData.image || assigneeData.avatar_url,
            avatar_url: assigneeData.assignee || assigneeData.image || assigneeData.avatar_url,
            user_email: assigneeData.user_email || assigneeData.email
        };
    };
    
    // Helper function to map multiple assignees
    const mapAssignees = (assigneesData) => {
        if (!assigneesData || !Array.isArray(assigneesData)) return [];
        return assigneesData.map(mapAssignee).filter(Boolean);
    };

    // Subtask handlers
    const handleAddSubtask = () => {
        setIsAddingSubtask(true);
        setTimeout(() => {
            if (subtaskInputRef.current) {
                subtaskInputRef.current.focus();
            }
        }, 100);
    };

    const handleSaveSubtask = (projectId, parentTaskId, taskListId) => {
        if (!subtaskTitle.trim() || !onCreateSubtask) return;
        // Use taskListId if available (SingleProjectView), otherwise use task.task_list_id or task.list_id (MyTasksView)
        onCreateSubtask(projectId, parentTaskId, taskListId, { title: subtaskTitle.trim() });
        setSubtaskTitle('');
        setIsAddingSubtask(false);

        setShowSubtasks(true);
    };

    const handleCancelSubtask = () => {
        setSubtaskTitle('');
        setIsAddingSubtask(false);
    };

    const handleSubtaskKeyDown = (e) => {
        if (e.key === 'Enter' && subtaskTitle.trim()) {
            e.preventDefault();
            handleSaveSubtask(projectId, task.id, taskListId);
        } else if (e.key === 'Escape') {
            handleCancelSubtask();
        }
    };

    const handleSubtaskBlur = (e) => {
        // Don't cancel if clicking on the save button
        if (e.relatedTarget && e.relatedTarget.closest('.asana-save-task-arrow-btn')) {
            return;
        }
        // Small delay to allow save button click to register
        setTimeout(() => {
            if (!subtaskInputRef.current || document.activeElement !== subtaskInputRef.current) {
                handleCancelSubtask();
            }
        }, 200);
    };

    // Get subtasks from task - handle both direct subtasks array and subtaskData
    const getSubtasks = () => {
        if (!task.subtasks || task.subtasks.length === 0) return [];
        return task.subtasks.map(subtask => {
            // If subtask has subtaskData, use that for more complete info
            if (subtask.subtaskData) {
                return {
                    ...subtask,
                    ...subtask.subtaskData,
                    // Preserve original assignees if available
                    assignees: subtask.assignees || subtask.subtaskData.assignees || []
                };
            }
            return subtask;
        });
    };
    const subtasks = getSubtasks();

    // Determine the list ID to use (for subtask operations)
    const currentTaskListId = taskListId || task.task_list_id || task.list_id;

    // Wrapper for onDelete to handle different signatures
    // TaskRow calls: onDelete(taskId, taskListId, projectId, parentTaskId)
    // SingleProjectView expects: onDelete(taskId, taskListId)
    // MyTasksView expects: onDelete(taskId, taskListId, projectId)
    const handleDelete = (projectId, taskId, taskListId, parentTaskId = null) => {
        if (onDelete) {
           onDelete(projectId, taskId, taskListId, parentTaskId);
        }
    };

    return (
        <>
            <TaskRow
                task={task}
                taskListId={taskListId}
                project={project}
                projectId={projectId}
                onUpdate={onUpdate}
                onDelete={handleDelete}
                showSubtasks={showSubtasks}
                setShowSubtasks={setShowSubtasks}
                addAssigneeToTask={addAssigneeToTask}
                searchUsers={searchUsers}
                onOpenDetail={onOpenDetail}
                isSubtask={false}
                handleAddSubtask={handleAddSubtask}
                subtasksCount={subtasks.length}
                onDragStart={onDragStart}
                onDragOver={onDragOver}
                onDrop={onDrop}
                onDragEnd={onDragEnd}
                isDragging={isDragging}
                isDragOver={isDragOver}
            />
            {/* Subtasks */}
            {showSubtasks && subtasks.length > 0 && subtasks.map((subtask) => (
                <TaskRow
                    key={subtask.id}
                    parentTaskData={task}
                    task={subtask}
                    taskListId={taskListId}
                    project={project}
                    projectId={projectId}
                    onUpdate={onUpdateSubtask ? (projectId, subtaskId, listId, data) => {
                        // SingleProjectView signature: onUpdateSubtask(subtaskId, parentTaskId, taskListId, subtaskData)
                        onUpdateSubtask(projectId, subtaskId, listId, data);
                    } : undefined}
                    onDelete={onDeleteSubtask ? (projectId, subtaskId, listId, parentTaskId) => {
                        // TaskRow calls: onDelete(subtaskId, taskListId, projectId, parentTaskId)
                        // Both handlers expect: onDeleteSubtask(subtaskId, parentTaskId, taskListId)
                        // So we need to reorder: (subtaskId, listId, projectIdParam, parentIdParam) -> (subtaskId, task.id, listId)
                        onDeleteSubtask(projectId, subtaskId, listId, parentTaskId);
                    } : undefined}
                    addAssigneeToTask={addAssigneeToTask}
                    searchUsers={searchUsers}
                    onOpenDetail={onOpenDetail}
                    isSubtask={true}
                    parentTaskId={task.id}
                    onDragStart={onDragStart}
                    onDragOver={onDragOver}
                    onDrop={onDrop}
                    onDragEnd={onDragEnd}
                    isDragging={false}
                    isDragOver={false}
                />
            ))}
            {/* Add Subtask Input Row */}
            {isAddingSubtask && (
                <tr className="asana-subtask-input-row asana-default-task-input-row">
                    <td colSpan="4" style={{ paddingLeft: '60px' }}>
                        <div className="asana-subtask-input-wrapper asana-task-name-cell">
                            <input
                                ref={subtaskInputRef}
                                type="text"
                                className="asana-subtask-input"
                                placeholder="Add a subtask"
                                value={subtaskTitle}
                                onChange={(e) => setSubtaskTitle(e.target.value)}
                                onKeyDown={handleSubtaskKeyDown}
                                onBlur={handleSubtaskBlur}
                                autoFocus
                            />
                            {subtaskTitle.trim() && (
                                <button
                                    type="button"
                                    className="asana-save-task-arrow-btn"
                                    onClick={(e) => {
                                        e.preventDefault();
                                        e.stopPropagation();
                                        handleSaveSubtask(projectId, task.id, taskListId);
                                    }}
                                    onMouseDown={(e) => {
                                        e.preventDefault(); // Prevent input blur
                                    }}
                                    title="Save subtask"
                                >
                                    <i className="fa-solid fa-check"></i>
                                </button>
                            )}
                        </div>
                    </td>
                </tr>
            )}
        </>
    );
};

export default EditableTaskRow;

